import { Controller } from '@hotwired/stimulus';
import { computePosition, flip, shift, offset, arrow, autoUpdate } from '@floating-ui/dom';

export default class FloatingUiController extends Controller {
  static targets = ['popup', 'trigger', 'arrow'];
  static values = {
    placement: { type: String, default: 'bottom' },
    offset: { type: Number, default: 8 },
    crossOffset: { type: Number, default: 0 },
    includeArrow: { type: Boolean, default: false },
    triggerType: { type: String, default: 'hover' }, // 'hover' or 'click'
  };

  connect() {
    this.isOpen = false;
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.positionFloatingElement = this.positionFloatingElement.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.toggle = this.toggle.bind(this);
    this.detectInvalidSetup();
    this.setupTrigger();
  }

  setupTrigger() {
    switch (this.triggerTypeValue) {
      case 'hover':
        this.triggerTarget.addEventListener('mouseenter', this.show)
        this.triggerTarget.addEventListener('mouseleave', this.hide)
        break
      case 'click':
        this.triggerTarget.addEventListener('click', this.toggle)
        break
      default:
        throw new Error(`Unsupported trigger type: ${this.triggerTypeValue}`)
    }
  }

  detectInvalidSetup() {
    if (!this.hasTriggerTarget) {
      throw new Error('Missing required configuration: trigger (target)')
    }

    if (!this.hasPopupTarget) {
      throw new Error('Missing required configuration: popup (target)')
    }

    // if blank value is supplied in DOM, default value will not be used
    if (this.placementValue === '') {
      throw new Error('Missing required configuration: placement (value)')
    }

    // if blank value is supplied in DOM, default value will not be used
    if (this.offsetValue === '') {
      throw new Error('Missing required configuration: offset (value)')
    }
  }

  fixupDOM() {
    if (this.includeArrowValue && !this.hasArrowTarget) {
      this.createDefaultArrowTarget()
    }
  }

  createDefaultArrowTarget() {
    const arrowTarget = document.createElement('span')
    arrowTarget.classList.add('tooltip-arrow')
    arrowTarget.setAttribute(`data-${this.identifier}-target`, 'arrow')
    arrowTarget.setAttribute('data-tooltip-arrow', '')
    this.popupTarget.appendChild(arrowTarget)
  }

  setupAutoUpdate() {
    this.cleanup = autoUpdate(this.triggerTarget, this.popupTarget, this.positionFloatingElement);
  }

  toggle() {
    this.isOpen ? this.hide() : this.show();
  }

  show() {
    this.isOpen = true;
    this.displayPopup();
    this.setupAutoUpdate();

    // Only a click-triggered popup stays open on its own; a hovered one closes on mouseleave.
    if (this.triggerTypeValue === 'click') {
      document.addEventListener('click', this.handleOutsideClick);
    }
  }

  hide() {
    this.isOpen = false;
    this.hidePopup();
    document.removeEventListener('click', this.handleOutsideClick);
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
  }

  // How the popup is shown and hidden. Subclasses whose popup is hidden by a stylesheet rather than by an
  // inline style override these; everything else in this controller works the same either way.
  displayPopup() {
    this.popupTarget.style.display = 'flex';
  }

  hidePopup() {
    this.popupTarget.style.display = 'none';
  }

  async positionFloatingElement() {
    if (!this.isOpen) {
      return;
    }

    const { x, y, middlewareData, placement } = await computePosition(this.triggerTarget, this.popupTarget, {
      placement: this.placementValue,
      middleware: [
        offset({ mainAxis: this.offsetValue, crossAxis: this.crossOffsetValue }),
        flip(),
        shift(),
        this.hasArrowTarget ? arrow({ element: this.arrowTarget }) : null,
      ].filter(Boolean),
    });

    Object.assign(this.popupTarget.style, {
      left: `${x}px`,
      top: `${y}px`,
    });

    if(this.includeArrowValue) {
      this.positionArrow(middlewareData.arrow, placement)
    }
  }

  handleOutsideClick(event) {
    if (!this.popupTarget.contains(event.target) && !this.triggerTarget.contains(event.target)) {
      this.hide();
    }
  }

  positionArrow(arrowData, popupPlacement) {
    if (!arrowData) {
      return
    }

    const {x, y} = arrowData;
    const arrowPlacement = this.arrowPlacement(popupPlacement)
    Object.assign(this.arrowTarget.style, {
      left: x != null ? `${x + (this.arrowTarget.offsetWidth / 3)}px` : '',
      top: y != null ? `${y - (this.arrowTarget.offsetWidth / 3)}px` : '',
      [arrowPlacement]: `${-this.arrowTarget.offsetWidth / 2}px`
    })
  }

  arrowPlacement(popupPlacement) {
    const placement = popupPlacement.split('-')[0]
    return {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[placement]
  }
}
